import {
  BRUSH_PRESETS,
  DEFAULT_STROKE_OPTIONS,
  FONT_STACKS,
  arrowHeadGeometry,
  boundsOfRects,
  connectorGeometry,
  drawWorldPoints,
  fitTextToBox,
  layoutText,
  nodeAABB,
  stripRichMarkup,
  pathToSvg,
  rectCenter,
  rectIntersects,
  shapePath,
  shapeTextRect,
  strokeOutline,
  textPositions,
  type BoardNode,
  type NodeLookup,
  type Rect,
  type TextStyle,
} from '@kanvas/board-core';
import type { BoardDoc } from '../collab/BoardDoc';
import { STICKY_PADDING, TEXT_PADDING, paintNode } from '../render/drawNode';
import { preloadImages } from '../render/imageCache';
import { measureText } from '../render/measure';
import { useEditor } from '../state/store';
import { expandForClone } from '../actions/clone';

export type ExportFormat = 'png' | 'svg' | 'json';

export interface ExportOptions {
  selectionOnly?: boolean;
  /** Device-pixel multiplier for raster output. */
  scale?: number;
  padding?: number;
  /** Transparent background for PNG and SVG. */
  transparent?: boolean;
}

/** Largest raster dimension, to stay inside browser canvas limits. */
const MAX_RASTER_EDGE = 12000;

export async function exportBoard(
  doc: BoardDoc,
  format: ExportFormat,
  options: ExportOptions = {},
): Promise<void> {
  const meta = doc.getMeta();
  const filename = meta.name.replace(/[^\w\-. ]+/g, '_').trim() || 'board';

  if (format === 'json') {
    const payload = JSON.stringify(doc.exportJSON(), null, 2);
    download(new Blob([payload], { type: 'application/json' }), `${filename}.json`);
    return;
  }

  const nodes = pickNodes(doc, options.selectionOnly ?? false);
  if (nodes.length === 0) return;

  const padding = options.padding ?? 32;
  const bounds = expand(
    boundsOfRects(nodes.map((n) => nodeAABB(n, doc.lookup))),
    padding,
  );

  // Images draw as placeholders until they are decoded, so wait for them first.
  const sources = nodes.filter((n) => n.type === 'image').map((n) => (n as { src: string }).src);
  if (sources.length > 0) await preloadImages(sources);

  if (format === 'svg') {
    const svg = toSvg(nodes, bounds, doc.lookup, options.transparent ? null : meta.background);
    download(new Blob([svg], { type: 'image/svg+xml' }), `${filename}.svg`);
    return;
  }

  const blob = await toPng(
    nodes,
    bounds,
    doc.lookup,
    options.transparent ? null : meta.background,
    options.scale ?? 2,
  );
  if (blob) download(blob, `${filename}.png`);
}

/**
 * Rasterises exactly the rectangle given — a frame's bounds, most often —
 * clipped so nothing outside it leaks in. This is what turns a frame into a
 * standalone "slide" image for the PDF, PPTX and print paths below: same
 * painter as the PNG/PPT export, just windowed to one frame instead of the
 * whole board, and with frames themselves left out since a slide should not
 * show its own border.
 */
export async function renderRectToCanvas(
  doc: BoardDoc,
  rect: Rect,
  options: { scale?: number; background?: string | null } = {},
): Promise<HTMLCanvasElement> {
  const nodes = doc
    .all()
    .filter(
      (n) =>
        n.type !== 'group' &&
        n.type !== 'comment' &&
        n.type !== 'frame' &&
        !n.hidden &&
        rectIntersects(nodeAABB(n, doc.lookup), rect),
    );

  const sources = nodes.filter((n) => n.type === 'image').map((n) => (n as { src: string }).src);
  if (sources.length > 0) await preloadImages(sources);

  const scale = Math.min(options.scale ?? 2, MAX_RASTER_EDGE / Math.max(rect.w, rect.h, 1));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(rect.w * scale));
  canvas.height = Math.max(1, Math.round(rect.h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const background = options.background === undefined ? doc.getMeta().background : options.background;
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvas.width, canvas.height);
  ctx.clip();
  ctx.setTransform(scale, 0, 0, scale, -rect.x * scale, -rect.y * scale);
  for (const node of nodes) paintNode(ctx, node, { lookup: doc.lookup, zoom: scale, forExport: true });
  ctx.restore();

  return canvas;
}

/**
 * Renders a set of nodes (e.g. a clipboard payload, which may come from
 * another board) to a transparent PNG at 2x, with `bounds` as the frame.
 * Connectors resolve their endpoints among the given nodes only.
 */
export async function renderNodesToPng(
  nodes: readonly BoardNode[],
  bounds: Rect,
): Promise<Blob | null> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const lookup: NodeLookup = (id) => byId.get(id);
  const sources = nodes.filter((n) => n.type === 'image').map((n) => (n as { src: string }).src);
  if (sources.length > 0) await preloadImages(sources);
  return toPng(nodes, expand(bounds, 4), lookup, null, 2);
}

function pickNodes(doc: BoardDoc, selectionOnly: boolean): BoardNode[] {
  const all = doc.all().filter((n) => n.type !== 'group' && n.type !== 'comment' && !n.hidden);
  if (!selectionOnly) return all;
  // A selected group stands for its members, which is what the user sees.
  const selection = new Set(
    expandForClone(useEditor.getState().selection, doc.all(), doc.lookup).map((n) => n.id),
  );
  return all.filter((n) => selection.has(n.id));
}

function expand(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, w: r.w + by * 2, h: r.h + by * 2 };
}

// ---------------------------------------------------------------------------
// PNG
// ---------------------------------------------------------------------------

async function toPng(
  nodes: readonly BoardNode[],
  bounds: Rect,
  lookup: NodeLookup,
  background: string | null,
  requestedScale: number,
): Promise<Blob | null> {
  // Clamp the scale so a huge board does not exceed the canvas size limit.
  const scale = Math.min(
    requestedScale,
    MAX_RASTER_EDGE / Math.max(bounds.w, bounds.h, 1),
  );

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bounds.w * scale));
  canvas.height = Math.max(1, Math.round(bounds.h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  ctx.setTransform(scale, 0, 0, scale, -bounds.x * scale, -bounds.y * scale);
  for (const node of nodes) {
    paintNode(ctx, node, { lookup, zoom: scale, forExport: true });
  }

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dashArray(style: string, width: number): string {
  if (style === 'dashed') return `${Math.max(width * 3, 6)} ${Math.max(width * 2.2, 5)}`;
  if (style === 'dotted') return `0.01 ${Math.max(width * 2.2, 4)}`;
  return 'none';
}

function transformFor(node: BoardNode): string {
  if (node.rotation === 0) return '';
  const c = rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h });
  const deg = (node.rotation * 180) / Math.PI;
  return ` transform="rotate(${deg.toFixed(3)} ${c.x.toFixed(2)} ${c.y.toFixed(2)})"`;
}

function textToSvg(rawText: string, style: TextStyle, box: Rect, autoFit: boolean): string {
  if (rawText === '') return '';
  // The SVG path does not (yet) render per-run bold/italic/link styling, so
  // the markup is stripped rather than shown as literal asterisks and
  // brackets — plain but honest, versus rich-looking punctuation noise.
  const text = stripRichMarkup(rawText);
  const layout = autoFit
    ? fitTextToBox(text, style, box, measureText)
    : layoutText(text, style, box.w, measureText);
  const positions = textPositions(layout, box, style);
  const family = FONT_STACKS[style.fontFamily] ?? FONT_STACKS.sans;

  const decoration = [style.underline ? 'underline' : '', style.strike ? 'line-through' : '']
    .filter(Boolean)
    .join(' ');

  const spans = layout.lines
    .map((line, i) => {
      if (line.text === '') return '';
      const p = positions[i]!;
      return `<text x="${p.x.toFixed(2)}" y="${p.y.toFixed(2)}">${esc(line.text)}</text>`;
    })
    .join('');

  return `<g font-family="${esc(family)}" font-size="${layout.fontSize}" font-weight="${
    style.bold ? 600 : 400
  }" font-style="${style.italic ? 'italic' : 'normal'}" fill="${style.color}"${
    decoration ? ` text-decoration="${decoration}"` : ''
  }>${spans}</g>`;
}

function nodeToSvg(node: BoardNode, lookup: NodeLookup): string {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  const opacity = node.opacity !== 1 ? ` opacity="${node.opacity}"` : '';
  const transform = transformFor(node);

  switch (node.type) {
    case 'sticky': {
      const textBox: Rect = {
        x: box.x + STICKY_PADDING,
        y: box.y + STICKY_PADDING,
        w: Math.max(box.w - STICKY_PADDING * 2, 1),
        h: Math.max(box.h - STICKY_PADDING * 2, 1),
      };
      return `<g${transform}${opacity}><rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="4" fill="${node.fill}"/>${textToSvg(
        node.text,
        node.textStyle,
        textBox,
        !node.autoGrow,
      )}</g>`;
    }

    case 'shape': {
      const d = pathToSvg(shapePath(node.shape, box, node.style.radius));
      const fill =
        node.style.fillStyle === 'solid' && node.style.fill !== 'transparent'
          ? node.style.fill
          : 'none';
      return `<g${transform}${opacity}><path d="${d}" fill="${fill}" stroke="${
        node.style.stroke
      }" stroke-width="${node.style.strokeWidth}" stroke-dasharray="${dashArray(
        node.style.strokeStyle,
        node.style.strokeWidth,
      )}" stroke-linejoin="round"/>${textToSvg(
        node.text,
        node.textStyle,
        shapeTextRect(node.shape, box),
        true,
      )}</g>`;
    }

    case 'text': {
      const bg = node.background
        ? `<rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="4" fill="${node.background}"/>`
        : '';
      return `<g${transform}${opacity}>${bg}${textToSvg(
        node.text,
        node.textStyle,
        { x: box.x + TEXT_PADDING, y: box.y, w: Math.max(box.w - TEXT_PADDING * 2, 1), h: box.h },
        false,
      )}</g>`;
    }

    case 'draw': {
      const world = drawWorldPoints(node);
      if (world.length === 0) return '';
      const preset = BRUSH_PRESETS[node.brush] ?? {};
      const outline = strokeOutline(
        world.map((p, i) => [p.x, p.y, node.points[i]?.[2] ?? 0.5] as [number, number, number]),
        { ...DEFAULT_STROKE_OPTIONS, ...preset, size: node.size },
      );
      if (outline.length < 2) return '';
      const d =
        `M${outline[0]!.x.toFixed(2)} ${outline[0]!.y.toFixed(2)}` +
        outline
          .slice(1)
          .map((p) => `L${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
          .join('') +
        'Z';
      // Same compositing as the canvas: the highlighter multiplies at 40% on
      // top of whatever opacity the stroke itself carries.
      const alpha =
        node.brush === 'highlighter'
          ? ` opacity="${(node.opacity * 0.4).toFixed(3)}" style="mix-blend-mode:multiply"`
          : opacity;
      return `<path${transform} d="${d}" fill="${node.color}"${alpha}/>`;
    }

    case 'connector': {
      const geo = connectorGeometry(node, lookup);
      const heads = [
        arrowHeadGeometry(node.startHead, geo.start, geo.startDir, node.strokeWidth),
        arrowHeadGeometry(node.endHead, geo.end, geo.endDir, node.strokeWidth),
      ]
        .filter((h) => h !== null)
        .map(
          (h) =>
            `<path d="${pathToSvg(h!.path)}" fill="${h!.filled ? node.stroke : 'none'}" stroke="${
              node.stroke
            }" stroke-width="${node.strokeWidth}" stroke-linejoin="round"/>`,
        )
        .join('');

      const label =
        node.text !== ''
          ? textToSvg(
              node.text,
              { ...node.textStyle, align: 'center', valign: 'middle' },
              { x: geo.mid.x - 90, y: geo.mid.y - 12, w: 180, h: 24 },
              false,
            )
          : '';

      return `<g${opacity}><path d="${pathToSvg(geo.path)}" fill="none" stroke="${
        node.stroke
      }" stroke-width="${node.strokeWidth}" stroke-dasharray="${dashArray(
        node.strokeStyle,
        node.strokeWidth,
      )}" stroke-linecap="round"/>${heads}${label}</g>`;
    }

    case 'image': {
      // The href keeps whatever the node stores: a data URL inlines, a served
      // asset stays a reference so the file does not balloon.
      //
      // A crop is a window onto the full picture, so the picture is placed at
      // the size and offset that puts the cropped region exactly over the
      // node box, and the box (or the freehand mask) clips it. Drawing the
      // whole source into the box, as before, exported cropped photos
      // squashed and uncropped.
      const c = node.crop ?? { x: 0, y: 0, w: 1, h: 1 };
      const srcW = box.w / Math.max(c.w, 1e-6);
      const srcH = box.h / Math.max(c.h, 1e-6);
      const src: Rect = { x: box.x - c.x * srcW, y: box.y - c.y * srcH, w: srcW, h: srcH };
      const img = `<image x="${src.x.toFixed(2)}" y="${src.y.toFixed(2)}" width="${src.w.toFixed(
        2,
      )}" height="${src.h.toFixed(2)}" href="${esc(node.src)}" preserveAspectRatio="none"/>`;
      const clipId = `clip-${node.id}`;
      let clip: string;
      if (node.mask && node.mask.length >= 3) {
        const d =
          node.mask
            .map((p, i) => {
              const x = box.x + ((p.x - c.x) / Math.max(c.w, 1e-6)) * box.w;
              const y = box.y + ((p.y - c.y) / Math.max(c.h, 1e-6)) * box.h;
              return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
            })
            .join('') + 'Z';
        clip = `<path d="${d}"/>`;
      } else if (node.crop || node.radius > 0) {
        clip = `<rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="${node.radius}"/>`;
      } else {
        return `<image${transform}${opacity} x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" href="${esc(
          node.src,
        )}" preserveAspectRatio="none"/>`;
      }
      return `<g${transform}${opacity}><defs><clipPath id="${clipId}">${clip}</clipPath></defs><g clip-path="url(#${clipId})">${img}</g></g>`;
    }

    case 'frame':
      return `<g${transform}${opacity}><rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" fill="${node.fill}" stroke="rgba(20,25,35,0.16)" stroke-width="1"/><text x="${box.x}" y="${
        box.y - 6
      }" font-family="Inter, system-ui, sans-serif" font-size="13" font-weight="600" fill="#6B7482">${esc(
        node.name,
      )}</text></g>`;

    case 'notebook':
      return `<g${transform}${opacity}><rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="6" fill="${node.coverColor}" stroke="rgba(20,25,35,0.16)" stroke-width="1"/><text x="${
        box.x + box.w / 2
      }" y="${box.y + box.h / 2}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="16" font-weight="700" fill="#1A1F28">${esc(
        node.title || 'Тетрадь',
      )}</text></g>`;

    case 'embed':
      return `<g${opacity}><rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="8" fill="#161A21"/><text x="${
        box.x + box.w / 2
      }" y="${box.y + box.h / 2}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="13" fill="#9AA3B2">${esc(
        node.title || node.url,
      )}</text></g>`;

    default:
      return '';
  }
}

function toSvg(
  nodes: readonly BoardNode[],
  bounds: Rect,
  lookup: NodeLookup,
  background: string | null,
): string {
  const body = nodes.map((n) => nodeToSvg(n, lookup)).join('\n  ');
  const bg = background
    ? `<rect x="${bounds.x}" y="${bounds.y}" width="${bounds.w}" height="${bounds.h}" fill="${background}"/>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${Math.round(bounds.w)}" height="${Math.round(bounds.h)}"
     viewBox="${bounds.x.toFixed(2)} ${bounds.y.toFixed(2)} ${bounds.w.toFixed(2)} ${bounds.h.toFixed(2)}">
  ${bg}
  ${body}
</svg>`;
}

// ---------------------------------------------------------------------------

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** PNG data URL of the whole board, used for board thumbnails. */
export async function renderThumbnail(doc: BoardDoc, maxEdge = 480): Promise<string | null> {
  const nodes = doc.all().filter((n) => n.type !== 'group' && n.type !== 'comment' && !n.hidden);
  if (nodes.length === 0) return null;
  const bounds = expand(boundsOfRects(nodes.map((n) => nodeAABB(n, doc.lookup))), 24);
  const scale = Math.min(1, maxEdge / Math.max(bounds.w, bounds.h, 1));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bounds.w * scale));
  canvas.height = Math.max(1, Math.round(bounds.h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const meta = doc.getMeta();
  ctx.fillStyle = meta.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(scale, 0, 0, scale, -bounds.x * scale, -bounds.y * scale);
  for (const node of nodes) paintNode(ctx, node, { lookup: doc.lookup, zoom: scale, forExport: true });

  return canvas.toDataURL('image/png');
}
